import React, { useState, useEffect } from 'react';
import { HazardCategory, FALLBACK_HAZARD_CATEGORIES } from '../types';
import { hazardService } from '../hazardService';
import { getActiveSessionOrganizationCode } from '../../../../shared/api';

interface HazardCategoriesTabProps {
  onCategoryCreated?: () => void;
}

export const HazardCategoriesTab: React.FC<HazardCategoriesTabProps> = ({ onCategoryCreated }) => {
  const [categories, setCategories] = useState<HazardCategory[]>(FALLBACK_HAZARD_CATEGORIES);
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const data = await hazardService.getCategories();
      const merged: HazardCategory[] = [];
      const seenCodes = new Set<string>();

      for (const c of (data || [])) {
        const key = (c.code || '').trim().toUpperCase();
        if (!seenCodes.has(key)) {
          seenCodes.add(key);
          merged.push(c);
        }
      }

      for (const f of FALLBACK_HAZARD_CATEGORIES) {
        const key = f.code.trim().toUpperCase();
        if (!seenCodes.has(key)) {
          seenCodes.add(key);
          merged.push(f);
        }
      }

      setCategories(merged);
    } catch (err: any) {
      console.error('Failed to load categories:', err);
      setCategories(FALLBACK_HAZARD_CATEGORIES);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleCreateCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanName = name.trim();
    let cleanCode = code.trim().toUpperCase();

    if (!cleanCode && cleanName) {
      cleanCode = cleanName.replace(/[^a-zA-Z0-9]/g, '_').replace(/_+/g, '_').toUpperCase().slice(0, 50);
    }

    if (!cleanCode || !cleanName) {
      alert('Please provide Category Code and Category Name.');
      return;
    }

    try {
      setCreating(true);
      await hazardService.createCategory({
        organizationId: getActiveSessionOrganizationCode() || undefined,
        code: cleanCode,
        name: cleanName,
        description: description.trim() || undefined,
      });
      setCode('');
      setName('');
      setDescription('');
      await fetchCategories();
      onCategoryCreated?.();
      alert('✓ Hazard taxonomy category successfully added.');
    } catch (err: any) {
      alert(`Failed to create category: ${err.message}`);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: '24px' }}>
      {/* Category List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div style={{ fontSize: '15px', fontWeight: 600, color: '#0f172a' }}>
          Active Hazard Taxonomies & Categories ({categories.length})
        </div>

        {categories.map((c) => (
            <div
              key={c.id}
              style={{
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                padding: '16px 20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                  <span
                    style={{
                      background: '#e0f2fe',
                      color: '#0369a1',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontWeight: 700,
                      fontSize: '12px',
                    }}
                  >
                    {c.code}
                  </span>
                  <span style={{ fontWeight: 600, fontSize: '14px', color: '#0f172a' }}>{c.name}</span>
                </div>
                {c.description && <div style={{ fontSize: '12px', color: '#64748b' }}>{c.description}</div>}
              </div>
              <span
                style={{
                  background: '#dcfce7',
                  color: '#15803d',
                  fontSize: '11px',
                  fontWeight: 600,
                  padding: '2px 8px',
                  borderRadius: '12px',
                }}
              >
                ACTIVE
              </span>
            </div>
        ))}
      </div>

      {/* Create Category Sidebar */}
      <div
        style={{
          background: '#ffffff',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          padding: '20px',
          alignSelf: 'start',
        }}
      >
        <h4 style={{ margin: '0 0 14px 0', fontSize: '15px', color: '#0f172a' }}>Add Hazard Taxonomy</h4>

        <form onSubmit={handleCreateCategory} style={{ display: 'flex', flexDirection: 'column', gap: '14px', fontSize: '13px' }}>
          <div>
            <label style={{ display: 'block', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
              Category Code *
            </label>
            <input
              type="text"
              required
              placeholder="e.g. CYBER or SECURITY" maxLength={100}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '4px',
                border: '1px solid #cbd5e1',
                boxSizing: 'border-box',
                textTransform: 'uppercase',
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
              Category Name *
            </label>
            <input
              type="text"
              required
              placeholder="e.g. Cyber & Information Security"
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '4px',
                border: '1px solid #cbd5e1',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontWeight: 600, color: '#334155', marginBottom: '4px' }}>
              Scope & Description
            </label>
            <textarea
              rows={3}
              placeholder="Operational boundary and examples of hazards in this category..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '4px',
                border: '1px solid #cbd5e1',
                boxSizing: 'border-box',
                fontFamily: 'inherit',
              }}
            />
          </div>

          <button
            type="submit"
            disabled={creating}
            style={{
              padding: '9px 16px',
              borderRadius: '6px',
              border: 'none',
              background: '#0284c7',
              color: '#ffffff',
              fontWeight: 600,
              cursor: creating ? 'not-allowed' : 'pointer',
              marginTop: '4px',
            }}
          >
            {creating ? 'Saving...' : 'Create Category'}
          </button>
        </form>
      </div>
    </div>
  );
};
